import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import type { ServiceRunStatus } from '@/types';
import { isServiceRunStatus } from '@/types';

export interface LineConnection {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  type: 'horizontal' | 'vertical';
  /** undefined means idle (no execution data). */
  status?: ServiceRunStatus;
  sourceNodeId?: string;
  targetNodeId?: string;
}

export interface JunctionPoint {
  x: number;
  y: number;
}

export function useHierarchyLines(
  containerRef: any,
  nodes: any,
  edges: any,
  runningNodes?: any,
  liveRunData?: any,
  effectiveStatusByService?: any
) {
  const lines = ref<LineConnection[]>([]);
  const junctions = ref<JunctionPoint[]>([]);
  const svgWidth = ref(0);
  const svgHeight = ref(0);

  // Helper function to determine line status based on node execution state
  function getLineStatus(sourceNodeId: string, targetNodeId: string): ServiceRunStatus | undefined {
    if (runningNodes?.value?.has(targetNodeId)) return 'Running';

    // Live run: check liveRunData map
    const execData = liveRunData?.value?.get(targetNodeId);
    if (execData && isServiceRunStatus(execData.status)) return execData.status;

    // History mode: prefer the shared effective-status map, which carries the inferred running frontier
    // (Pending + parents settled → Running) so the line into the currently-executing service animates
    // blue exactly as the canvas does. Falls back to the raw node status when no map is supplied.
    const effective = effectiveStatusByService?.value?.get(targetNodeId);
    if (isServiceRunStatus(effective)) return effective;

    const node = nodes.value?.find((n: any) => n.id === targetNodeId);
    const runStatus = node?.data?.run?.status;
    if (isServiceRunStatus(runStatus)) return runStatus;

    return undefined;
  }

  function calculateLines() {
    if (!containerRef.value) return;

    const container = containerRef.value;
    const containerRect = container.getBoundingClientRect();
    const newLines: LineConnection[] = [];
    const newJunctions: JunctionPoint[] = [];

    // Find all node cards
    const nodeElements = container.querySelectorAll('[data-node-id]');

    nodeElements.forEach((nodeEl: Element) => {
      const nodeId = nodeEl.getAttribute('data-node-id');
      if (!nodeId) return;

      const nodeRect = nodeEl.getBoundingClientRect();
      const nodeLeft = nodeRect.left - containerRect.left;
      const nodeTop = nodeRect.top - containerRect.top;
      const nodeMiddle = nodeTop + 24; // Middle of the card header (where connections happen)

      // Find parent edges for this node
      const parentEdges = edges.value.filter((e: any) => e.target === nodeId);

      if (parentEdges.length === 0) {
        // Root node - short horizontal lead-in stub with a dot, marking the top of
        // the tree. Kept in step with the child hop (RAIL_INSET) so the root stub and
        // the parent→child elbows read at the same length.
        const rootLineLength = 12;
        const rootConnectionX = nodeLeft - rootLineLength;

        // Determine root node status
        const rootStatus: ServiceRunStatus | undefined = getLineStatus(nodeId, nodeId);

        newLines.push({
          x1: rootConnectionX,
          y1: nodeMiddle,
          x2: nodeLeft,
          y2: nodeMiddle,
          type: 'horizontal',
          status: rootStatus,
          sourceNodeId: nodeId, // Root node is both source and target
          targetNodeId: nodeId
        });

        // Add a connector circle at the start of the line (far left)
        newJunctions.push({
          x: rootConnectionX,
          y: nodeMiddle
        });
      }

      parentEdges.forEach((edge: any) => {
        const parentEl = container.querySelector(`[data-node-id="${edge.source}"]`);
        if (!parentEl) return;

        const parentRect = parentEl.getBoundingClientRect();
        const parentTop = parentRect.top - containerRect.top;

        // The vertical rail sits just left of the CHILD's edge (not out in the gutter
        // to the parent's left), so it drops from under the parent card's bottom and
        // elbows into the child. Tighter and easier to read than a trunk that exits the
        // parent's side — mirrors the nested-runs POC (railX = childLeft - RAIL_INSET).
        const RAIL_INSET = 10; // how far left of the child's edge the shared rail sits
        const railX = nodeLeft - RAIL_INSET;

        // Start the vertical at the parent card's BOTTOM edge, not its middle: railX sits
        // inside the parent's horizontal span (childLeft - 10 = parentLeft + 10), and the
        // SVG overlay paints behind the opaque cards — so a rail starting at the header
        // would be hidden behind the card. Emerging from the bottom edge is both visible
        // and the intended "line comes out the bottom of the parent" look.
        const parentBottom = parentTop + parentRect.height;

        // Connection point on parent's vertical line
        const parentConnectionX = railX;
        const parentConnectionY = parentBottom;

        // Connection point on child (left edge of panel)
        const childConnectionX = nodeLeft;
        const childConnectionY = nodeMiddle;

        // Determine line status based on execution state
        const lineStatus = getLineStatus(edge.source, nodeId);

        // Draw vertical line from parent down to child level
        newLines.push({
          x1: parentConnectionX,
          y1: parentConnectionY,
          x2: parentConnectionX,
          y2: childConnectionY,
          type: 'vertical',
          status: lineStatus,
          sourceNodeId: edge.source,
          targetNodeId: nodeId
        });

        // Draw horizontal line from vertical line to child panel
        newLines.push({
          x1: parentConnectionX,
          y1: childConnectionY,
          x2: childConnectionX,
          y2: childConnectionY,
          type: 'horizontal',
          status: lineStatus,
          sourceNodeId: edge.source,
          targetNodeId: nodeId
        });

        // Add junction point where vertical meets horizontal
        newJunctions.push({
          x: parentConnectionX,
          y: childConnectionY
        });
      });
    });

    lines.value = newLines;
    junctions.value = newJunctions;
    svgWidth.value = containerRect.width;
    svgHeight.value = containerRect.height;
  }

  let resizeObserver: ResizeObserver | null = null;

  onMounted(() => {
    nextTick(() => {
      calculateLines();

      // Recalculate on resize
      resizeObserver = new ResizeObserver(() => {
        calculateLines();
      });

      if (containerRef.value) {
        resizeObserver.observe(containerRef.value);
      }
    });
  });

  onUnmounted(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
  });

  // Watch for changes in nodes/edges/execution state
  watch(
    [nodes, edges, () => runningNodes?.value, () => liveRunData?.value, () => effectiveStatusByService?.value],
    () => {
      nextTick(() => {
        calculateLines();
      });
    },
    { deep: true }
  );

  return {
    lines,
    junctions,
    svgWidth,
    svgHeight,
    recalculateLines: calculateLines
  };
}
